import { ERROR_COPY, isTechnicalMessage } from "./errorMessages";

/**
 * Pure classification of failed requests, kept free of axios so it can be reasoned about
 * and unit tested on its own. Applied centrally by the response interceptor in
 * `services/authorizationService.js`.
 */

/** The three backends disagree on the field: Ruby uses message/error, FastAPI uses detail. */
export const pickBackendMessage = data => {
    if (!data || typeof data !== "object") return null;
    if (typeof data.message === "string") return data.message;
    if (typeof data.error === "string") return data.error;
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.detail) && typeof data.detail[0]?.msg === "string") return data.detail[0].msg;
    return null;
};

/**
 * Map a failed request onto a copy key, so callers can decide severity instead of guessing
 * it from message text. Backend wording is preserved where it helps the user (validation)
 * and replaced where it is technical.
 */
export const classifyApiError = error => {
    // A request the app itself abandoned is not a failure worth showing or recording.
    if (error?.code === "ERR_CANCELED") return { key: "CANCELED" };
    if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) return { key: "TIMEOUT" };
    if (error?.code === "ERR_NETWORK" || !error?.response) return { key: "OFFLINE" };

    const { status, data } = error.response;
    const backend = pickBackendMessage(data);

    if (status === 400 || status === 422) return { key: "VALIDATION", message: backend };
    // Covers both an expired session and a rejected login — the backend's own wording wins
    // when there is one, which is what a failed sign-in needs.
    if (status === 401) return { key: "SESSION", message: backend };
    if (status === 403) return { key: "FORBIDDEN" };
    if (status === 404) return { key: "NOT_FOUND" };
    // Infra gave up rather than the app failing — say so in timeout terms.
    if (status === 408 || status === 504) return { key: "TIMEOUT" };
    if (status === 502 || status === 503) return { key: "UNAVAILABLE" };
    if (status >= 500) return { key: "SERVER", message: isTechnicalMessage(backend) ? null : backend };
    // Remaining 4xx (409 conflict, 429 rate limit, …): the user or their input caused it, so
    // it is expected traffic rather than an incident to alert on.
    if (status >= 400) return { key: "CLIENT", message: backend };

    return { key: "GENERIC", message: backend };
};

export const getApiErrorMessage = error => {
    const { key, message } = classifyApiError(error);
    return message || ERROR_COPY[key];
};

/**
 * Publishes the resolved message under every field name a call site might read, so no call site
 * needs its own fallback for the field its backend happens to omit.
 *
 * Arrays and objects are left untouched: FastAPI returns `detail` as a list of field errors, and
 * some reducers keep objects under `error`. Originals are preserved under `_raw`.
 */
export const withResolvedMessage = (data, friendly, ref) => {
    const next = { ...data, message: friendly, ref, _raw: data };
    if (data.detail === undefined || typeof data.detail === "string") next.detail = friendly;
    if (data.error === undefined || typeof data.error === "string") next.error = friendly;
    return next;
};

/**
 * Some endpoints report failure inside a 200 response as `success: false`. No HTTP error occurs, so
 * the rejection path never runs and nothing guarantees the call site has text to show — which left a
 * failed action showing no alert at all. Give those the same treatment, so an app-level failure is
 * as dependable as an HTTP one. Genuine successes are untouched.
 */
export const withAppFailureMessage = data => {
    if (!data || typeof data !== "object" || Array.isArray(data)) return data;
    if (data.success !== false) return data;
    return withResolvedMessage(data, pickBackendMessage(data) || ERROR_COPY.GENERIC);
};
